import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Plus, Tag, Layers, Trash2, Box, Store, Star, Edit2, ChevronLeft, ChevronRight, UploadCloud, X, Database, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CatalogosProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [productos, setProductos] = useState([]);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  
  // Handlers de Exportación
  const handleExport = (format) => {
    if (productos.length === 0) {
      toast.error("No hay registros para exportar");
      return;
    }

    const dataToExport = productos.map(p => {
      const base = {
        SKU: p.sku || p._id,
        Nombre: p.nombre,
        Descripcion: p.descripcion,
        Precio: p.precio,
        Categoria: p.categoria,
        Stock: p.cantidad,
        Estado: p.estado,
        Fecha_Registro: p.fecha_registro
      };
      // Incluir campos personalizados
      customFields.forEach(f => {
        base[f.label] = p[f.name] || '';
      });
      return base;
    });

    const fileName = `Export_Catalogos_${tenantSlug}_${new Date().toISOString().split('T')[0]}`;

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.json`;
      a.click();
    } else if (format === 'csv') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.csv`;
      a.click();
    } else if (format === 'xlsx') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }
    
    setIsExportMenuOpen(false);
    toast.success(`Exportación ${format.toUpperCase()} iniciada`);
  };
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [editingRecordId, setEditingRecordId] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    sku: '',
    nombre: '',
    descripcion: '',
    precio: '',
    categoria: '',
    cantidad: '',
    estado: 'ACTIVO'
  });
  const [customFields, setCustomFields] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkFormat, setBulkFormat] = useState("json");
  const [isInjecting, setIsInjecting] = useState(false);

  useEffect(() => {
    fetchProductos();
  }, [tenantSlug]);

  const fetchProductos = async () => {
    try {
      setLoading(true);
      // Fetch data
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/catalogos`);
      setProductos(res.data || []);
      
      // Fetch silo config to get custom fields
      const silosRes = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos`);
      const catSilo = silosRes.data.find(s => s.name === 'catalogos');
      if (catSilo && catSilo.custom_fields) {
        setCustomFields(catSilo.custom_fields);
      }
    } catch (err) {
      console.error(err);
      toast.error('Garantice que haya corrido el Activador de Kit 5x5 o creado el silo "catalogos"');
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.precio) {
      toast.error("El Nombre y Precio son obligatorios");
      return;
    }
    
    try {
      const payload = {
        data: {
          sku: formData.sku || '',
          nombre: formData.nombre,
          descripcion: formData.descripcion || '',
          precio: parseFloat(formData.precio) || 0,
          categoria: formData.categoria || 'General',
          cantidad: parseInt(formData.cantidad) || 0,
          estado: formData.estado,
          fecha_registro: new Date().toISOString()
        }
      };

      // Inyectar campos personalizados si existen
      customFields.forEach(f => {
        if (formData[f.name]) {
          payload.data[f.name] = formData[f.name];
        }
      });

      if (editingRecordId) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/catalogos/${editingRecordId}`, payload.data);
        toast.success("Producto modificado correctamente");
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/catalogos/inject`, payload);
        toast.success("Producto inyectado a los catálogos");
      }

      setIsSidebarOpen(false);
      setEditingRecordId(null);
      setFormData({ nombre: '', precio: '', categoria: '', cantidad: '', estado: 'ACTIVO' });
      fetchProductos();
    } catch (err) {
      toast.error("Error al publicar el producto");
    }
  };

  const handleEdit = (prod) => {
    setEditingRecordId(prod._id);
    const editData = {
      sku: prod.sku || '',
      nombre: prod.nombre || '',
      descripcion: prod.descripcion || '',
      precio: prod.precio || '',
      categoria: prod.categoria || '',
      cantidad: prod.cantidad || '',
      estado: prod.estado || 'ACTIVO'
    };
    
    // Poblar campos personalizados
    customFields.forEach(f => {
      editData[f.name] = prod[f.name] || '';
    });
    
    setFormData(editData);
    setIsSidebarOpen(true);
  };

  const openNewForm = () => {
    setEditingRecordId(null);
    setFormData({ sku: '', nombre: '', descripcion: '', precio: '', categoria: '', cantidad: '', estado: 'ACTIVO' });
    setIsSidebarOpen(true);
  };

  const handleDelete = async (recordId) => {
    if(!window.confirm("¿Seguro que deseas eliminar permanentemente este producto del catálogo?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/catalogos/${recordId}`);
      toast.success("Producto retirado");
      fetchProductos();
    } catch (err) {
      toast.error("Error al eliminar producto");
    }
  };

  const handleBulkInject = async () => {
    if (!bulkText.trim()) {
      toast.error("El campo está vacío");
      return;
    }
    
    setIsInjecting(true);
    try {
      let dataToInject = [];
      if (bulkFormat === 'json') {
        dataToInject = JSON.parse(bulkText);
        if (!Array.isArray(dataToInject)) dataToInject = [dataToInject];
      } else {
        const rows = bulkText.split('\n');
        const headers = rows[0].split(',').map(h => h.trim());
        dataToInject = rows.slice(1).filter(r => r.trim()).map(row => {
          const values = row.split(',');
          let obj = {};
          headers.forEach((header, i) => {
            obj[header] = values[i]?.trim();
          });
          return obj;
        });
      }

      await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/catalogos/inject`, dataToInject);
      toast.success(`${dataToInject.length} registros inyectados correctamente`);
      setBulkText("");
      setIsBulkModalOpen(false);
      fetchProductos();
    } catch (err) {
      console.error(err);
      toast.error("Error en la inyección masiva: " + err.message);
    } finally {
      setIsInjecting(false);
    }
  };

  const filteredProductos = productos.filter(p => 
    p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcula Estadísticas de Inventario
  const activos = productos.filter(p => (p.estado || '').toUpperCase() === 'ACTIVO').length;
  
  const totalPrecio = productos.reduce((acc, current) => acc + (parseFloat(current.precio) || 0), 0);
  const promedioPrecio = productos.length > 0 ? (totalPrecio / productos.length) : 0;

  // Status Badge Styling
  const getStatusColor = (status) => {
    switch((status || '').toUpperCase()) {
      case 'ACTIVO': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'AGOTADO': return 'bg-rose-500/20 text-rose-400 border-rose-500/50';
      case 'OCULTO': return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
      default: return 'bg-white/10 text-slate-300 border-white/20';
    }
  };

  return (
    <div className="h-full flex flex-col pt-8 lg:pt-0 pb-20 lg:pb-0">
      {/* Premium Amber Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-amber-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Store className="w-4 h-4 text-amber-400" />
                <span className="text-[9px] uppercase tracking-[0.4em] font-black text-amber-400">Knowledge Base</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-rose-500">Catálogos</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Librería de productos y servicios sincronizada con IA.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button 
                    onClick={() => document.getElementById('catalogos-table-container').scrollBy({ left: -300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button 
                    onClick={() => document.getElementById('catalogos-table-container').scrollBy({ left: 300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={openNewForm}
                  className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Nueva</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Catalog KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-amber-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-amber-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-amber-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
            <Package className="w-8 h-8 text-amber-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{productos.length}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Total de SKUs</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-emerald-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
            <Box className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{activos}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Disp. Inmediata</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-orange-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-orange-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-orange-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20">
            <Tag className="w-8 h-8 text-orange-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">${promedioPrecio.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Precio Promedio</p>
          </div>
        </div>
      </div>

      {/* Modern Table Layout */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-[2rem] flex-1 overflow-hidden flex flex-col">
          <div id="catalogos-table-container" className="overflow-x-auto custom-scrollbar relative">
            <table className="w-full min-w-[1200px] text-left border-collapse">
              <thead className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-md">
                <tr className="bg-white/5 border-b border-white/5">
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Producto / Servicio</th>
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Descripción</th>
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Categoría</th>
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Precio / Stock</th>
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Distribución</th>
                  {customFields.map(f => (
                    <th key={f.name} className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {f.label}
                    </th>
                  ))}
                  <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Manejo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                   <tr>
                     <td colSpan="5" className="p-12 text-center text-slate-500 text-sm font-medium">Construyendo catálogos base...</td>
                   </tr>
                ) : filteredProductos.length === 0 ? (
                   <tr>
                     <td colSpan="5" className="p-20 text-center">
                        <Package className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                        <p className="text-lg text-slate-400 font-medium">Bóveda vacía</p>
                        <p className="text-sm text-slate-500 mt-1">Sube tus productos/servicios a la derecha para que la IA los recomiende.</p>
                     </td>
                   </tr>
                ) : (
                  filteredProductos.map((prod, index) => (
                    <motion.tr 
                      key={prod._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-white/[0.02] transition-colors group"
                    >
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                           <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center border border-white/10 shrink-0 text-white shadow-lg">
                              {prod.precio >= 1000 ? <Star className="w-4 h-4 text-white" /> : <Box className="w-4 h-4 text-white" />}
                           </div>
                           <div>
                             <p className="text-white font-bold text-sm tracking-wide">{prod.nombre || 'SKU sin nombre'}</p>
                             <p className="text-amber-400/80 text-[10px] font-black uppercase tracking-widest mt-1">SKU: {prod.sku || prod._id?.substring(0, 8) || 'N/A'}</p>
                           </div>
                        </div>
                      </td>
                      <td className="p-6">
                         <p className="text-slate-400 text-xs line-clamp-2 max-w-[200px] leading-relaxed italic">
                           {prod.descripcion || 'Sin descripción...'}
                         </p>
                      </td>
                      <td className="p-6">
                         <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-slate-500" />
                            <span className="text-slate-300 font-medium text-sm">{prod.categoria || 'Sin Clasificar'}</span>
                         </div>
                      </td>
                      <td className="p-6">
                         <div className="flex flex-col">
                            <span className="text-amber-400 font-black text-lg">${parseFloat(prod.precio || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">{prod.cantidad || 0} unidades</span>
                         </div>
                      </td>
                      <td className="p-6">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(prod.estado)}`}>
                          {prod.estado || 'ACTIVO'}
                        </span>
                      </td>
                      {customFields.map(f => (
                        <td key={f.name} className="p-6">
                           <span className="text-slate-400 text-xs font-bold italic">
                             {prod[f.name] || '—'}
                           </span>
                        </td>
                      ))}
                      <td className="p-6 text-right whitespace-nowrap">
                         <button 
                            onClick={() => handleEdit(prod)}
                            className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm mr-2"
                            title="Modificar SKU"
                          >
                           <Edit2 className="w-4 h-4" />
                         </button>
                         <button 
                            onClick={() => handleDelete(prod._id)}
                            className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm"
                            title="Descontinuar SKU"
                          >
                           <Trash2 className="w-4 h-4" />
                         </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Right Sidebar Form for Data Inject */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 h-full w-full max-w-[450px] bg-[#0F172A] border-l border-white/10 z-[101] shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Sidebar Header */}
              <div className="p-6 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${editingRecordId ? 'bg-amber-500/10 border-amber-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                            <Package className={`w-5 h-5 ${editingRecordId ? 'text-amber-400' : 'text-orange-400'}`} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{editingRecordId ? 'Modificar SKU' : 'Publicar SKU'}</h2>
                            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5 italic">Catálogo Fluxo</p>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-500 transition-colors">
                        <Plus className="w-7 h-7 rotate-45" />
                    </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                  {/* Basic Info Group */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-amber-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Identificación & Nombre</span>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Código SKU</label>
                        <input 
                          type="text" 
                          className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white focus:border-amber-500/50 outline-none transition-all font-black placeholder:text-slate-800 italic text-xs"
                          value={formData.sku}
                          onChange={e => setFormData({...formData, sku: e.target.value})}
                          placeholder="Ej. LAPTOP-HP-001"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-amber-500 ml-1 italic">Nombre del Producto *</label>
                        <input 
                          type="text" 
                          className="w-full bg-amber-500/[0.02] border border-amber-500/20 rounded-xl py-2.5 px-4 text-white focus:border-amber-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                          value={formData.nombre}
                          onChange={e => setFormData({...formData, nombre: e.target.value})}
                          placeholder="Ej. Consultoría..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description Group */}
                  <div className="space-y-6 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-orange-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Detalles & Categoría</span>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Descripción Detallada</label>
                        <textarea 
                          className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white focus:border-orange-500/50 outline-none transition-all min-h-[100px] resize-none font-medium placeholder:text-slate-800 text-xs leading-relaxed italic"
                          value={formData.descripcion}
                          onChange={e => setFormData({...formData, descripcion: e.target.value})}
                          placeholder="Escribe aquí los detalles..."
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Categoría Base</label>
                        <input 
                          type="text" 
                          className="w-full bg-black/40 border border-white/5 rounded-xl py-3 px-4 text-white focus:border-orange-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                          value={formData.categoria}
                          onChange={e => setFormData({...formData, categoria: e.target.value})}
                          placeholder="Ej. Ropa..."
                        />
                    </div>
                  </div>

                  {/* Price & Inventory Group */}
                  <div className="space-y-6 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-emerald-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Precio & Disponibilidad</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-emerald-500 ml-1 italic">Precio Unitario *</label>
                            <div className="relative group">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 font-black italic">$</span>
                                <input 
                                    type="number" 
                                    className="w-full bg-emerald-500/[0.02] border border-emerald-500/20 rounded-xl py-2.5 pl-8 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-black text-xs italic"
                                    value={formData.precio}
                                    onChange={e => setFormData({...formData, precio: e.target.value})}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Stock Inicial</label>
                            <input 
                                type="number" 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all font-black text-xs italic"
                                value={formData.cantidad}
                                onChange={e => setFormData({...formData, cantidad: e.target.value})}
                                placeholder="0"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Estado de Publicación</label>
                        <select 
                            className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-amber-500/50 outline-none transition-all appearance-none font-black text-xs uppercase tracking-[0.15em] italic"
                            value={formData.estado}
                            onChange={e => setFormData({...formData, estado: e.target.value})}
                        >
                            <option value="ACTIVO">Activo (En stock)</option>
                            <option value="AGOTADO">Agotado temporalmente</option>
                            <option value="OCULTO">Oculto del menú digital</option>
                        </select>
                    </div>

                    {/* Custom Fields in Form */}
                    {customFields.length > 0 && (
                        <div className="space-y-6 pt-6 border-t border-white/5">
                            {customFields.map((f) => (
                                <div key={f.name} className="space-y-1.5">
                                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">{f.label}</label>
                                    <input 
                                        type={f.type === 'number' || f.type === 'currency' ? 'number' : 'text'}
                                        className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-amber-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                        value={formData[f.name] || ''}
                                        onChange={e => setFormData({...formData, [f.name]: e.target.value})}
                                        placeholder={`Ingresa ${f.label.toLowerCase()}...`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                  </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 border-t border-white/5 bg-white/[0.02] flex items-center gap-4">
                  <div className="flex-1 p-3 rounded-xl bg-amber-500/5 border border-amber-500/10 flex items-center gap-3">
                      <Database className="w-5 h-5 text-amber-400" />
                      <div>
                          <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest italic">Catálogo Maestro</p>
                          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter italic">Sincronización</p>
                      </div>
                  </div>
                  <button 
                    onClick={handleSave}
                    className="flex-[2] py-4 bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-500 hover:to-orange-600 text-white font-black uppercase tracking-[0.15em] text-[10px] rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 italic"
                  >
                    <Download className="w-4 h-4 rotate-180" />
                    <span>{editingRecordId ? 'Guardar Cambios' : 'Publicar en Catálogo'}</span>
                  </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <AnimatePresence>
        {isBulkModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              onClick={() => setIsBulkModalOpen(false)}
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-slate-900 border border-white/10 w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative z-10 overflow-hidden"
            >
              <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Database className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">Carga Masiva</h3>
                    <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Inyección directa al Silo Catalogos</p>
                  </div>
                </div>
                <button onClick={() => setIsBulkModalOpen(false)} className="p-3 hover:bg-white/10 rounded-2xl text-slate-400 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8">
                <div className="flex gap-4 mb-6 bg-black/40 p-2 rounded-2xl border border-white/5">
                  <button 
                    onClick={() => setBulkFormat('json')}
                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${bulkFormat === 'json' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-white'}`}
                  >
                    JSON Array
                  </button>
                  <button 
                    onClick={() => setBulkFormat('csv')}
                    className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${bulkFormat === 'csv' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-white'}`}
                  >
                    CSV (Comas)
                  </button>
                </div>

                <textarea 
                  className="w-full h-64 bg-black/60 border border-white/10 rounded-3xl p-6 text-blue-400 font-mono text-sm focus:border-blue-500 outline-none transition-all resize-none shadow-inner"
                  placeholder={bulkFormat === 'json' ? '[{"nombre": "Prod A", "precio": 100}, ...]' : 'nombre, precio, categoria\nProd A, 100, General'}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                />

                <div className="mt-8 flex gap-4">
                  <button 
                    onClick={() => setIsBulkModalOpen(false)}
                    className="flex-1 py-4 bg-white/5 hover:bg-white/10 text-white font-black uppercase tracking-widest text-xs rounded-2xl transition-all border border-white/10"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleBulkInject}
                    disabled={isInjecting}
                    className="flex-[2] py-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-2"
                  >
                    {isInjecting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Confirmar Inyección
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CatalogosProPage;
